import { Currency } from '../enums/currency-enum';
import { getLocaleForCurrency } from './currency';
import { isNullish, isNullishOrEmptyString } from './nullish.js';

const LOCALE = 'en-us'; //Locale Service is Not Implemented Yet - Set the locale to en-us as default for now
const CURRENCY = 'currency';
const USD = 'USD';
const PERCENT = 'percent';
const COMPACT = 'compact';
const SHORT = 'short';
const EXCEPT_ZERO = 'exceptZero';

type NumArg = number | null | undefined;

/**
 * Value to use when no data provided or is null or undefined
 */
export const NO_VALUE_DATA = 'N/A';

/**
 * Returns the value passed in if it's not null or undefined
 * otherwise an empty string.
 *
 * This avoids null/undefined getting printed in certain circumstances.
 */
export function valueOrEmptyString(value: string | null | undefined) {
  return isNullishOrEmptyString(value) ? '' : value;
}

/**
 * Some basic sanity checks that we can display a number
 */
export function numberIsPrintable(value: number): boolean {
  return !isNullish(value) && !isNaN(value) && isFinite(value);
}

/**
 * General format with locale that returns a string, uses locale
 * returns N/A if it's null/undefined
 *
 * Example usage: generalFormattedNumber(123456789.123) -> 123,456,789.123
 */
export function generalFormattedNumber(value: NumArg): string {
  if (typeof value === 'number' && numberIsPrintable(value)) {
    return new Intl.NumberFormat(LOCALE).format(value);
  }
  return NO_VALUE_DATA;
}

/**
 * Returns a number as a string, no formatting
 * returns N/A if it's null/undefined
 *
 * Example usage: numberAsString(123456789.123) -> 123456789.123
 */
export function numberAsString(value: NumArg): string {
  if (typeof value === 'number' && numberIsPrintable(value)) {
    return value.toString();
  }
  return NO_VALUE_DATA;
}

/**
 * Formats a number with a specific number of decimal places, uses locale
 * returns N/A if it's null/undefined
 *
 * Example usage: formatToNDecimalPlaces(123456789.123, 2) -> 123,456,789.12
 */

export function formatToNDecimalPlaces(
  value: NumArg,
  decimalPlaces: number,
  customNoValueSymbol?: string,
): string {
  if (typeof value === 'number' && numberIsPrintable(value)) {
    return new Intl.NumberFormat(LOCALE, {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }).format(value);
  }
  return customNoValueSymbol ?? NO_VALUE_DATA;
}

/**
 * Formats a number as a percentage with a specific number of decimal places, uses locale
 * returns N/A if it's null/undefined
 *
 * Example usage: formatPercentageToNDecimalPlaces(0.456789, 2) -> 45.68%
 */
export function formatPercentageToNDecimalPlaces(
  value: NumArg,
  decimalPlaces: number,
): string {
  if (typeof value === 'number' && numberIsPrintable(value)) {
    return new Intl.NumberFormat(LOCALE, {
      style: PERCENT,
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }).format(value);
  }
  return NO_VALUE_DATA;
}

/**
 * Formats a number as a percentage that is already in decimal form with a specific number of decimal places, uses locale
 * returns N/A if it's null/undefined
 *
 * Example usage: formatDecimalAsPercentage(45.6789, 2) -> 45.68%
 */
export function formatDecimalAsPercentage(
  value: NumArg,
  decimalPlaces: number,
): string {
  if (typeof value === 'number' && numberIsPrintable(value)) {
    return formatPercentageToNDecimalPlaces(value / 100, decimalPlaces);
  }
  return NO_VALUE_DATA;
}

/**
 * Abbreviates a number with a specific number of decimal places, uses locale
 * If no decimal places are provided, it will default to 0
 * returns N/A if it's null/undefined
 *
 * Example usage: abbreviateNumberToNDecimalPlaces(123456789, 2) -> 123.46M
 */
export function abbreviateNumber(value: NumArg, decimalPlaces = 0): string {
  if (typeof value === 'number' && numberIsPrintable(value)) {
    return new Intl.NumberFormat(LOCALE, {
      notation: COMPACT,
      compactDisplay: SHORT,
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }).format(value);
  }
  return NO_VALUE_DATA;
}

/**
 * Abbreviate a number to a provided currency format with a specific number of decimal places, uses locale
 * returns N/A if it's null/undefined
 *
 * Example usage: abbreviateNumberToCurrency(123456789, 'EUR', 2) -> €123.46M
 */
export function abbreviateNumberToCurrency(
  value: NumArg,
  currency: string,
  decimalPlaces: number,
): string {
  if (typeof value === 'number' && numberIsPrintable(value)) {
    return new Intl.NumberFormat(LOCALE, {
      style: CURRENCY,
      currency: currency,
      notation: COMPACT,
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }).format(value);
  }
  return NO_VALUE_DATA;
}

/**
 * Abbreviate a number to USD currency format with 2 decimal places, uses locale
 * returns N/A if it's null/undefined
 *
 * Example usage: abbreviateNumberToUSD(123456789) -> $123.46M
 */
export function abbreviateNumberToUSD(value: NumArg) {
  if (typeof value === 'number' && numberIsPrintable(value)) {
    return abbreviateNumberToCurrency(value, USD, 2);
  }
  return NO_VALUE_DATA;
}

/**
 * Formats a number to a specific currency, uses locale
 * returns N/A if it's null/undefined
 *
 * Example usage: formatNumberToCurrency(123456789.123, 'EUR') -> €123,456,789.123
 */
export function formatNumberToCurrency(
  value: NumArg,
  currency: Currency,
  customNoValueSymbol?: string,
) {
  if (typeof value === 'number' && numberIsPrintable(value)) {
    return new Intl.NumberFormat(getLocaleForCurrency(currency), {
      style: CURRENCY,
      currency: currency,
    }).format(value);
  }
  return customNoValueSymbol ?? NO_VALUE_DATA;
}

/**
 * Formats a number with a dollar sign, uses US locale
 * returns N/A if it's null/undefined
 *
 * Example usage: formatNumberToUSDCurrency(123456789.123) -> $123,456,789.12
 */
export function formatNumberToUSDCurrency(value: NumArg) {
  return formatNumberToCurrency(value, USD);
}

/**
 * Rounds a number to a specific number of decimal places, returns a string
 * returns N/A if it's null/undefined
 *
 * Example usage: roundNumberToNDecimalPlaces(123.456789, 2) -> 123.46
 */
export function roundNumberToNDecimalPlaces(
  value: NumArg,
  decimalPlaces: number,
): string {
  if (typeof value === 'number' && numberIsPrintable(value)) {
    return value.toFixed(decimalPlaces);
  }
  return NO_VALUE_DATA;
}

/**
 * Formats a number as scientific notation with a specific number of decimal places, returns a string
 * returns N/A if it's null/undefined
 *
 * Example usage: formatNumberAsScientificNotation(123456789, 2) -> 1.23e+8
 */
export function formatNumberAsScientificNotation(
  value: NumArg,
  decimalPlaces: number,
): string {
  if (typeof value === 'number' && numberIsPrintable(value)) {
    return value.toExponential(decimalPlaces);
  }
  return NO_VALUE_DATA;
}

/**
 * Formats a number to its ordinal form, returns a string
 * returns N/A if it's null/undefined
 *
 * Example usage: formatNumberAsOrdinal(123) -> 123rd
 */
export function formatNumberAsOrdinal(value: NumArg): string {
  if (typeof value === 'number' && numberIsPrintable(value)) {
    const suffix = ['th', 'st', 'nd', 'rd'];
    const v = value % 100;
    return value + (suffix[(v - 20) % 10] || suffix[v] || suffix[0]);
  }
  return NO_VALUE_DATA;
}

/**
 * Formats a number to its significant digits, uses locale
 * returns N/A if it's null/undefined
 *
 * Example usage: formatNumberToSignificantDigits(123456789, 5) -> 123,460,000
 */
export function formatNumberToSignificantDigits(
  value: NumArg,
  significantDigits: number,
): string {
  if (typeof value === 'number' && numberIsPrintable(value)) {
    return new Intl.NumberFormat(LOCALE, {
      minimumSignificantDigits: significantDigits,
      maximumSignificantDigits: significantDigits,
    }).format(value);
  }
  return NO_VALUE_DATA;
}

/**
 * Formats a number to show its sign, uses locale, 2 decimal places
 * returns N/A if it's null/undefined
 *
 * Example usage: formatNumberToShowSign(123456789) -> +123,456,789.00
 */

export function formatNumberToShowSign(value: NumArg) {
  if (typeof value === 'number' && numberIsPrintable(value)) {
    return Intl.NumberFormat(LOCALE, {
      signDisplay: EXCEPT_ZERO,
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }).format(value);
  }
  return NO_VALUE_DATA;
}

/**
 * Converts a pixel value to a numeric value.
 * @param pxValue - The pixel value to convert.
 * @returns The numeric value extracted from the pixel value.
 * @throws Error if the provided value does not end with 'px'.
 */
export function getNumericValueFromPx(pxValue: string): number {
  if (!pxValue.endsWith('px')) {
    throw new Error(`Expected a pixel value but received ${pxValue}`);
  }
  return Number(pxValue.replace('px', ''));
}
